import { registerPlugin } from '@capacitor/core';
export interface AppBuildConfig {
  apiBaseUrl?: string;
  pairingCode?: string;
}

export class ApiClient {
  private token = '';
  readonly baseUrl: string;
  readonly available: boolean;

  constructor(config: AppBuildConfig, nativePlatform: boolean) {
    this.baseUrl = String(config.apiBaseUrl || '').replace(/\/+$/, '');
    this.available = !nativePlatform || this.baseUrl.length > 0;
  }

  async initializeSession(config: AppBuildConfig, nativePlatform: boolean): Promise<void> {
    if (!nativePlatform || !this.available) return;
    const secure = registerPlugin<{ read(): Promise<{ token: string }>; save(options: { token: string }): Promise<void> }>('SecureSession');
    this.token = (await secure.read()).token;
    if (!this.token && config.pairingCode) {
      await this.pair(config.pairingCode, true);
    }
  }

  async pair(code: string, nativePlatform: boolean): Promise<void> {
    const session = await this.json<{ token: string }>('/api/auth/pair', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code })
    });
    if (nativePlatform) {
      const secure = registerPlugin<{ save(options: { token: string }): Promise<void> }>('SecureSession');
      await secure.save({ token: session.token });
      this.token = session.token;
    }
  }

  async fetch(path: string, init: RequestInit = {}): Promise<Response> {
    if (!this.available) {
      throw new Error('Server API není pro tento mobilní build nakonfigurované.');
    }
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const url = this.baseUrl ? `${this.baseUrl}${normalizedPath}` : normalizedPath;
    const headers = new Headers(init.headers);
    if (this.token) headers.set('Authorization', `Bearer ${this.token}`);
    // UI language for AI answers and voice (public/js/i18n.js); the server defaults to Czech.
    const locale = (globalThis as { appLocale?: () => string }).appLocale?.();
    if (locale && !headers.has('X-App-Locale')) headers.set('X-App-Locale', locale);
    return globalThis.fetch(url, {
      ...init,
      headers,
      signal: init.signal || AbortSignal.timeout(normalizedPath.startsWith('/api/ai/') ? 180000 : 15000),
      credentials: init.credentials || 'include'
    });
  }

  async json<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await this.fetch(path, init);
    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      throw new Error(`API ${response.status}${detail ? `: ${detail.slice(0, 200)}` : ''}`);
    }
    return response.json() as Promise<T>;
  }
}
